/** @format */

import React, { useEffect, useMemo, useState } from 'react';
import { useMeetingAttendance, useMeetingActivity, formatLogTimestampGhana } from '../hooks/useMeetingLogs';
import { useUsers } from '../hooks/useUsers';

interface MeetingDetailsTabsProps {
  meetingId: string;
}

const MeetingDetailsTabs: React.FC<MeetingDetailsTabsProps> = ({ meetingId }) => {
  const [activeTab, setActiveTab] = useState<'joined' | 'activity'>('joined');

  const { participantIds } = useMeetingAttendance(meetingId);
  const { logs } = useMeetingActivity(meetingId, 20);
  const { fetchUsers, getUserDisplayName, getUserAvatar } = useUsers();

  useEffect(() => {
    if (participantIds.length > 0) fetchUsers(participantIds);
  }, [participantIds, fetchUsers]);

  const participantNames = useMemo(() => {
    return participantIds.map((id) => ({ id, name: getUserDisplayName(id), avatar: getUserAvatar(id) }));
  }, [participantIds, getUserDisplayName, getUserAvatar]);

  return (
    <div className="mt-3 text-xs">
      {/* Horizontal Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-3">
        <button
          onClick={() => setActiveTab('joined')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'joined'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Joined ({participantNames.length})
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'activity'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Activity ({logs.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'joined' && (
        <div>
          {participantNames.length === 0 ? (
            <div className="text-gray-400">No participants recorded</div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {participantNames.map((p) => (
                <li key={p.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                  {p.avatar ? (
                    <img src={p.avatar} alt={p.name} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[10px] text-gray-700">
                      {p.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span className="truncate">{p.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div>
          {logs.length === 0 ? (
            <div className="text-gray-400">No activity yet</div>
          ) : (
            <ul className="space-y-1 text-gray-600">
              {logs.map((log) => (
                <li key={log.id} className="flex items-center gap-2">
                  <span className="capitalize">{log.action.replace('_', ' ')}</span>
                  <span>•</span>
                  <span>{formatLogTimestampGhana(log.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingDetailsTabs;


